/**
 * Filters for field event summaries.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { DATE_RANGE_PERIOD_OPTIONS, EVENT_SUMMARY_STATUS_CONFIG } from './event-summary-configs';
import type { EventSummaryFilters } from '@/types/event-summary.types';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';

/** Props for {@link EventSummaryFiltersContent}. */
interface EventSummaryFiltersContentProps {
  filters: EventSummaryFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetDateRange: (range: EventSummaryFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEstablishment: (id: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Period chips; picking one clears the custom date range so the two stay exclusive. */
const PeriodFilter: React.FC<{
  filters: EventSummaryFilters;
  onSetDateRange: (range: EventSummaryFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Période</span>
    <SegmentedControl
      size="sm"
      value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
      onChange={(v) => {
        onSetDateRange(v as EventSummaryFilters['dateRange']);
        onSetCustomDateRange(undefined, undefined);
      }}
      options={[...DATE_RANGE_PERIOD_OPTIONS]}
    />
  </div>
);

/** Custom from/to dates; setting either forces `dateRange` back to `all`. */
const DateRangeFilter: React.FC<{
  filters: EventSummaryFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: EventSummaryFilters['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  /** Local YYYY-MM-DD (not UTC) so the date picker does not shift a day. */
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  /** Empty value clears the start; a date starts at 00:00 local. */
  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    onSetCustomDateRange(new Date(year, month - 1, day, 0, 0, 0), filters.customDateTo);
    onSetDateRange('all');
  };

  /** Empty value clears the end; a date ends at 23:59:59 local. */
  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    onSetCustomDateRange(filters.customDateFrom, new Date(year, month - 1, day, 23, 59, 59));
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Multi-select status chips (`not_filled` / `filled`). */
const StatusFilter: React.FC<{
  filters: EventSummaryFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(EVENT_SUMMARY_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key as any);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Single-establishment autocomplete (stored as a one-id array). */
const EstablishmentFilter: React.FC<{
  filters: EventSummaryFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();
  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <Autocomplete
        placeholder="Rechercher..."
        options={options}
        onSearch={onSearch}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        value={filters.establishmentIds?.[0]}
        onChange={(val) => onSetEstablishment(val)}
        inputSize="sm"
      />
    </div>
  );
};

/** Filters for field event summaries. */
export const EventSummaryFiltersContent: React.FC<EventSummaryFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onSetDateRange,
  onSetCustomDateRange,
  onSetEstablishment,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}

      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
    </div>
  );
};
